/**
 * Fetches every product owned by the current buyer and joins it against the
 * full list of linked LoanApplications to produce a `PropertyView[]`. The
 * computed list respects the user's chosen filter + sort from
 * `propertyUiSlice`.
 *
 * For sellers, listings come from SellerContract records instead of products.
 *
 * PropertyView itself is **derived** — see `src/utils/propertyView/buildPropertyView.ts`.
 */

import { useEffect, useMemo, useState } from 'react';
import type { RootState } from '@/store';
import { useSelector } from 'react-redux';

import { USER_ROLE } from '@/config/constants';
import { useGetMyBuyerContractsQuery } from '@/apis/extended/contractApi';
import { useGetMyLoanApplicationsQuery } from '@/apis/extended/loanApplicationApi';
import { ALL_PRODUCTS, useGetProductsQuery } from '@/apis/extended/productApi';
import { useGetAllSellerContractsQuery } from '@/apis/extended/sellerAPI';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { useEnrichmentWatch } from '@/hooks/useEnrichmentWatch';
import type {
  PropertyFilter,
  PropertySort,
} from '@/store/slices/propertyUiSlice';
import { pickApplicationFor } from '@/utils/loanApplicationPick';
import { ENRICHMENT_POLL_MS } from '@/utils/propertyEnrichment';
import { buildPropertyView } from '@/utils/propertyView/buildPropertyView';
import { resolveSellerStage } from '@/utils/propertyView/sellerStageResolver';
import {
  dedupeSellerContractRows,
  normalizeSellerContractRow,
} from '@/utils/sellerContract/normalizeSellerProduct';
import type { Contract } from '@/types/ContractType';
import type { Product } from '@/types/ProductType';
import type { PropertyView } from '@/types/PropertyView';
import type { SellerContractRecord } from '@/types/SellerContractType';

function matchesFilter(p: PropertyView, filter: PropertyFilter): boolean {
  if (p.pipelineMode === 'seller') return filter === 'all';
  switch (filter) {
    case 'purchase':
      return !p.isRefinance;
    case 'refinance':
      return p.isRefinance;
    case 'dscr':
      return p.isDscr;
    default:
      return true;
  }
}

function sortFn(sort: PropertySort) {
  return (a: PropertyView, b: PropertyView) => {
    switch (sort) {
      case 'stage':
        return a.stage - b.stage;
      case 'price':
        return (b.listPrice ?? 0) - (a.listPrice ?? 0);
      case 'updated':
      default: {
        const aDate = new Date(a.stageEnteredAt).getTime();
        const bDate = new Date(b.stageEnteredAt).getTime();
        return bDate - aDate;
      }
    }
  };
}

function buildSellerProperties(
  rows: SellerContractRecord[] | undefined
): PropertyView[] {
  if (!rows?.length) return [];
  const deduped = dedupeSellerContractRows(rows, (c) =>
    resolveSellerStage({ contract: c })
  );
  return deduped.map((row) => {
    const { product, contract, contractId } = normalizeSellerContractRow(row);
    return buildPropertyView({
      product,
      contract,
      pipelineMode: 'seller',
      contractIdOverride: contractId,
    });
  });
}

export function useProperties(): {
  properties: PropertyView[];
  allProperties: PropertyView[];
  isLoading: boolean;
  /** Normalized product snapshots for seller listings (keyed by product id). */
  sellerProductsById: Map<string, Product>;
} {
  const { data: user } = useGetCurrentUserQuery();
  const isSeller = user?.role === USER_ROLE.SELLER;

  // Keep the grid fresh while a newly added property is still being enriched.
  const [pollEnrichment, setPollEnrichment] = useState(false);
  const { data: products, isLoading: productsLoading } = useGetProductsQuery(
    ALL_PRODUCTS,
    {
      skip: isSeller,
      pollingInterval: pollEnrichment ? ENRICHMENT_POLL_MS : 0,
    }
  );
  const anyEnriching = useEnrichmentWatch(
    isSeller ? undefined : products?.products
  );
  useEffect(() => {
    setPollEnrichment(anyEnriching);
  }, [anyEnriching]);
  const { data: applications, isLoading: appsLoading } =
    useGetMyLoanApplicationsQuery(undefined, { skip: isSeller });
  // Cards need the offer to show Stages 6–10; the product list only carries
  // the contract id.
  const { data: buyerContracts } = useGetMyBuyerContractsQuery(undefined, {
    skip: isSeller,
  });
  const { data: sellerContracts, isLoading: sellerContractsLoading } =
    useGetAllSellerContractsQuery(user?._id ?? '', {
      skip: !isSeller || !user?._id,
    });

  const filter = useSelector((s: RootState) => s.propertyUi.filter);
  const sort = useSelector((s: RootState) => s.propertyUi.sort);

  const sellerProductsById = useMemo(() => {
    const map = new Map<string, Product>();
    if (!isSeller || !sellerContracts) return map;
    for (const row of sellerContracts as unknown as SellerContractRecord[]) {
      const { product } = normalizeSellerContractRow(row);
      map.set(product._id, product);
    }
    return map;
  }, [isSeller, sellerContracts]);

  const allProperties = useMemo<PropertyView[]>(() => {
    if (isSeller) {
      return buildSellerProperties(
        sellerContracts as unknown as SellerContractRecord[] | undefined
      );
    }
    const list = products?.products ?? [];
    const apps = applications ?? [];
    // A property can have several contracts (old drafts, a voided offer, the
    // live one). Last-write-wins let a stale one tag a ratified deal as
    // "Negotiating"; prefer the product's linked contract, then a ratified
    // one, then the most recently updated.
    const contractsByProduct = new Map<string, Contract[]>();
    for (const c of buyerContracts ?? []) {
      const pid = (c as any)?.product?._id ?? (c as any)?.product;
      if (!pid) continue;
      const key = String(pid);
      contractsByProduct.set(key, [...(contractsByProduct.get(key) ?? []), c]);
    }
    const pickContract = (product: Product): Contract | undefined => {
      const candidates = contractsByProduct.get(product._id) ?? [];
      const linkedId =
        typeof product.contract === 'string'
          ? product.contract
          : (product.contract as any)?._id;
      const linked = candidates.find(
        (c) => linkedId && String(c._id) === String(linkedId)
      );
      if (linked) return linked;
      const time = (c: Contract) =>
        new Date((c.updatedAt || c.createdAt || 0) as string).getTime();
      const rank = (c: Contract) =>
        c.isRatified || c.contractStatus === 'ratified'
          ? 2
          : c.contractStatus === 'voided'
          ? 0
          : 1;
      return [...candidates].sort(
        (a, b) => rank(b) - rank(a) || time(b) - time(a)
      )[0];
    };
    return list.map((product) =>
      buildPropertyView({
        product,
        application: pickApplicationFor(product, apps),
        contract: pickContract(product),
        user,
        pipelineMode: 'buyer',
      })
    );
  }, [
    isSeller,
    sellerContracts,
    products?.products,
    applications,
    buyerContracts,
    user,
  ]);

  const properties = useMemo(() => {
    return [...allProperties]
      .filter((p) => matchesFilter(p, filter))
      .sort(sortFn(sort));
  }, [allProperties, filter, sort]);

  return {
    properties,
    allProperties,
    isLoading: isSeller
      ? sellerContractsLoading
      : productsLoading || appsLoading,
    sellerProductsById,
  };
}
